import { useCallback, useMemo, useRef } from 'react';
import { Product } from '@interfaces/models/product';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { getProductTrackingData } from '@helpers/utils/catalog/catalog-tracking-utils';
import { debounce } from '@helpers/utils/general';

interface UseProductSliderImpressionTrackingProps {
  productFeedCategory: string;
  productFeedPosition?: string;
  trackingPayload?: object;
}

const useProductSliderImpressionTracking = ({
  productFeedCategory,
  productFeedPosition,
  trackingPayload,
}: UseProductSliderImpressionTrackingProps) => {
  const { sendAnalyticEvent } = useAnalyticEvents('product');

  // Tracking state refs (refs avoid re-renders and prevent stale closures)
  const highestSeenIndexRef = useRef<number>(-1);
  const trackedProductIdsRef = useRef<Set<string>>(new Set());
  const hasInitiallyRevealedRef = useRef(false);
  const allProductsRef = useRef<Product[]>([]);

  const buildProductTrackingData = useCallback(
    (product: Product, position: number) => ({
      ...getProductTrackingData(product),
      product_position: String(position + 1),
      product_feed_category: productFeedCategory,
      product_feed_position: productFeedPosition ?? '',
    }),
    [productFeedCategory, productFeedPosition],
  );

  const sendImpressionEvent = useCallback(
    (products: Product[], startingPosition: number) => {
      if (products.length === 0) {
        return;
      }

      sendAnalyticEvent('product_impression', {
        ...trackingPayload,
        product_card_details: products.map((product, index) =>
          buildProductTrackingData(product, startingPosition + index),
        ),
      });

      products.forEach((product) => trackedProductIdsRef.current.add(product.id));
    },
    [sendAnalyticEvent, trackingPayload, buildProductTrackingData],
  );

  const trackNewlyVisibleProducts = useMemo(
    () =>
      debounce(() => {
        if (!hasInitiallyRevealedRef.current) {
          return;
        }

        const visibleProducts = allProductsRef.current.slice(0, highestSeenIndexRef.current + 1);
        const untrackedProducts = visibleProducts.filter((product) => !trackedProductIdsRef.current.has(product.id));

        if (untrackedProducts.length > 0) {
          const startingPosition = trackedProductIdsRef.current.size;
          sendImpressionEvent(untrackedProducts, startingPosition);
        }
      }, 50),
    [sendImpressionEvent],
  );

  const onVisibilityChange = useCallback(
    ({ isVisible, index, products }: { isVisible: boolean; index: number; products: Product[] }) => {
      if (!isVisible || index <= highestSeenIndexRef.current) {
        return;
      }

      highestSeenIndexRef.current = index;
      allProductsRef.current = products;

      trackNewlyVisibleProducts();
    },
    [trackNewlyVisibleProducts],
  );

  const onProductClick = useCallback(
    (clickedProduct: Product, index: number) => {
      sendAnalyticEvent('product_click', {
        ...trackingPayload,
        product_card_details: buildProductTrackingData(clickedProduct, index),
      });
    },
    [sendAnalyticEvent, trackingPayload, buildProductTrackingData],
  );

  const triggerInitialRevealTracking = useCallback(() => {
    if (hasInitiallyRevealedRef.current) {
      return;
    }

    hasInitiallyRevealedRef.current = true;

    const visibleProducts = allProductsRef.current.slice(0, highestSeenIndexRef.current + 1);

    if (visibleProducts.length > 0) {
      sendImpressionEvent(visibleProducts, 0);
    }
  }, [sendImpressionEvent]);

  return { onVisibilityChange, onProductClick, triggerInitialRevealTracking };
};

export default useProductSliderImpressionTracking;
